/**
 * Wave 6.2 — Adverse-Action review queue.
 *
 * Lives under `/rcc/adverse-action-queue` and is also available as a top-level
 * compliance entry. Renders rows from
 * `client.retail.carts.complianceQueue('pending')` — the backend already
 * humanizes the origin tier label and resolves officer display names so the
 * UI stays presentational.
 *
 * Per brief:
 *   - Customer name column shows "Anonymous" for fsr-self carts that closed
 *     with no PII captured.
 *   - "Open compliance review" routes to `/compliance/adverse-review/{cartId}`
 *     where a thin landing scaffolds the review entry point. Actual
 *     letter generation is Wave 6.3.
 *   - No Cart IDs / GUIDs surface in the UI.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router';
import { Button, Pill } from '@/components/primitives';
import { useApi } from '@/modules/RAO/hooks/useApi';
import type {
  ComplianceQueueEntityType,
  ComplianceQueueRow,
} from '@retail-ops/shared-types';

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; rows: ReadonlyArray<ComplianceQueueRow> }
  | { kind: 'error'; message: string };

const ENTITY_TYPE_TABS: ReadonlyArray<{
  value: ComplianceQueueEntityType;
  label: string;
}> = [
  { value: 'cart', label: 'Account opening' },
  { value: 'maint-action', label: 'Maintenance' },
  { value: 'all', label: 'All' },
];

export function AdverseActionQueue(): JSX.Element {
  const navigate = useNavigate();
  const { client, makeSignal } = useApi();
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Wave 6.3 — entity-type filter so the same surface serves both
  // cart AA and MaintAction AA reviews.
  const [entityType, setEntityType] = useState<ComplianceQueueEntityType>('cart');

  useEffect(() => {
    const signal = makeSignal();
    setState({ kind: 'loading' });
    client.retail.carts.complianceQueue('pending', { signal, entityType }).then(
      (res) => {
        const anyRes = res as unknown as {
          success?: boolean;
          data?: ComplianceQueueRow[];
          error?: string;
        };
        if (anyRes?.success === false) {
          setState({ kind: 'error', message: anyRes.error ?? 'Unable to load queue.' });
          return;
        }
        setState({ kind: 'ok', rows: anyRes.data ?? [] });
      },
      (err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Unable to load queue.',
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  return (
    <div>
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-8)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <Link
          to="/rcc"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            fontSize: 12,
            color: 'var(--pcb-blue)',
            textDecoration: 'none',
            width: 'fit-content',
          }}
        >
          <ChevronLeft size={14} /> Back to Compliance Center
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)' }}>
          <h1 style={{ margin: 0 }}>Adverse-Action queue</h1>
          <Pill variant="info">FSR · Compliance</Pill>
          {state.kind === 'ok' && (
            <Pill variant={state.rows.length > 0 ? 'warn' : 'ok'}>
              {state.rows.length} pending
            </Pill>
          )}
        </div>
        <p style={{ margin: 0, color: 'var(--ink-600)', fontSize: 13 }}>
          Records closed with resolution code <strong>Adverse-Action</strong> across all origin
          tiers. Open a row to review the decline.
        </p>
        <div
          role="tablist"
          aria-label="Compliance queue scope"
          style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}
        >
          {ENTITY_TYPE_TABS.map((t) => {
            const active = entityType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={active ? 'true' : 'false'}
                onClick={() => setEntityType(t.value)}
                style={{
                  padding: 'var(--sp-2) var(--sp-4)',
                  fontSize: 12,
                  background: active ? 'var(--brand-fill)' : 'transparent',
                  color: active ? 'var(--on-brand)' : 'var(--pcb-blue)',
                  border: '1px solid var(--pcb-blue)',
                  borderRadius: 'var(--r-md)',
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {state.kind === 'loading' && <QueueSkeleton />}

      {state.kind === 'error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-6)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          {state.message}
        </div>
      )}

      {state.kind === 'ok' && state.rows.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-10)',
            textAlign: 'center',
            background: 'var(--surface-card)',
            border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink-700)',
            fontSize: 14,
          }}
        >
          No adverse-action reviews pending. Carts closed with the Adverse-Action resolution
          code will surface here for after-the-fact compliance review.
        </div>
      )}

      {state.kind === 'ok' && state.rows.length > 0 && (
        <div
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--ink-100)', textAlign: 'left' }}>
                <Th>Branch</Th>
                <Th>Customer</Th>
                <Th>Origin</Th>
                <Th>Resolved by</Th>
                <Th>Resolved at</Th>
                <Th>Reason</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <Row
                  key={row.cartId}
                  row={row}
                  onOpen={() => navigate(`/compliance/adverse-review/${row.cartId}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: 'var(--ink-600)',
        borderBottom: '1px solid var(--surface-border)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Row({
  row,
  onOpen,
}: {
  row: ComplianceQueueRow;
  onOpen: () => void;
}): JSX.Element {
  const customer = row.customerName ?? 'Anonymous';
  const isAnonymous = !row.customerName;
  const officer = row.resolvedByDisplayName ?? row.resolvedByOfficerCode ?? '—';
  const branch = row.branchName ?? row.branch;
  const resolvedAt = formatDateTime(row.resolvedAt);

  return (
    <tr style={{ borderBottom: '1px solid var(--surface-border)' }}>
      <Td>{branch}</Td>
      <Td>
        {customer}
        {isAnonymous && (
          <span
            style={{ marginLeft: 'var(--sp-3)', fontSize: 11, color: 'var(--ink-500)' }}
            title="No PII was captured before this cart was resolved."
          >
            (no PII)
          </span>
        )}
        {row.entityType === 'maint-action' && (
          <span style={{ marginLeft: 'var(--sp-3)' }}>
            <Pill variant="info">Maintenance</Pill>
          </span>
        )}
      </Td>
      <Td>
        <Pill variant="muted">{row.originTierLabel}</Pill>
      </Td>
      <Td>{officer}</Td>
      <Td style={{ whiteSpace: 'nowrap', color: 'var(--ink-600)' }}>{resolvedAt}</Td>
      <Td>
        <span
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            maxWidth: 320,
          }}
          title={row.resolutionReason}
        >
          {row.resolutionReason}
        </span>
      </Td>
      <Td>
        <Button variant="primary" size="sm" onClick={onOpen}>
          Open compliance review
        </Button>
      </Td>
    </tr>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <td
      style={{
        padding: 'var(--sp-4) var(--sp-5)',
        verticalAlign: 'top',
        color: 'var(--ink-800)',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function QueueSkeleton(): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-7)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 36,
            background: 'var(--ink-100)',
            borderRadius: 'var(--r-sm)',
            opacity: 0.7,
          }}
        />
      ))}
    </div>
  );
}
